/**
 * BenchmarkSelector component module.
 * @module advisor/components/benchmarks/BenchmarkSelector
 *
 */
import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import debug from 'debug';
import {layoutAttribute} from '../cssUtils';
import {DescriptiveText, Dropdown} from '../../index';
import {formatter} from '../../utils/utils';
import Promise from 'bluebird';
import axiosInstance from '../../utils/axios';
import lazy from 'lazy.js';

const log = debug('circleblack:benchmarkSelector');
const percentFormatter = formatter('percentDecimal', {decimalPlaces: 0});

/**
 * Benchmark selector based on the Dropdown component
 * @name BenchmarkSelector
 */
const BenchmarkSelector = props => {
  const {
    session,
    width,
    maxWidth,
    onChange,
    showDisclosure,
    layout,
  } = props;

  const [loading, setLoading] = useState(true);
  const [benchmark, setBenchmark] = useState(null);
  const [benchmarkOptions, setBenchmarkOptions] = useState([]);

  const formatSecurities = securities => securities.map(s => `${s.securityName} (${percentFormatter(s.weighting)})`).join(', ');

  const getBenchmarks = () => Promise.join(
    axiosInstance.get('/benchmarks', {params: {sort: '+typeGroupSeq,+displayName'}}),
    session?.profile?.id && axiosInstance.get(`/preferences/advisors/${session.profile.id}/panels/advisorPreferences`),
    function getOptions(benchmarkRes, preferenceRes) {
      if (!(benchmarkRes && preferenceRes)) {return;}

      const benchmarks = benchmarkRes.data;
      const preferences = preferenceRes.data;

      const options = [
        {label: 'None', value: {id: null, displayName: 'None'}},
        ...lazy(benchmarks).groupBy('typeGroupName').toArray().map(type => ({
          label: type[0],
          options: type[1].map(b => ({label: b.displayName, value: b})),
        })),
      ];
      setBenchmarkOptions(options);

      const defaultBenchmarkID = preferences.collections
        .find(collection => collection.title === 'performanceType')
        .groups.find(group => group.title === 'defaultPerformanceBenchmark')
        .cores.find(core => core.title === 'defaultPerformanceBenchmark')
        .val;

      const selection = benchmarks.find(b => b.id === defaultBenchmarkID) || options[0].value;
      setBenchmark(selection);
      onChange(selection);
      setLoading(false);
    }
  );

  useEffect(() => {
    getBenchmarks();
  }, [session]);

  log('rendering');
  return <Dropdown
    title={'Benchmark'}
    placeholder={'Select a Benchmark...'}
    dataPoint={{name: 'benchmark', value: benchmark && {label: benchmark.displayName, value: benchmark}}}
    options={benchmarkOptions}
    isLoading={loading}
    onChange={(name, option) => {
      setBenchmark(option.value);
      onChange(option.value);
    }}
    isClearable={false}
    isCreatable={false}
    isSearchable={true}
    width={width}
    maxWidth={maxWidth}
    layout={layout}
    help={showDisclosure && {
      type: 'modal',
      title: 'Benchmark Disclosure',
      size: 'md',
      content: <DescriptiveText preset={'paragraph'}>
        {benchmark?.id
          ? `The performance of the selected benchmark is based on the returns of ${formatSecurities(benchmark.securities)}. Standard & Poor’s®, S&P® and SPDR® are registered trademarks of Standard & Poor’s Financial Services LLC (S&P).  CircleBlack, Inc. is not affiliated, related or under license with Standard & Poor’s Financial Services LLC.`
          : 'Select a benchmark in the dropdown menu to see its details and disclosures.'
        }
      </DescriptiveText>,
    }}
  />;
};

const mapStateToProps = state => ({
  session: state.session,
});

export default connect(mapStateToProps, null)(BenchmarkSelector);

BenchmarkSelector.propTypes = {
  /**
   * User Session, includes token and email.
   */
  session: PropTypes.shape({profile: PropTypes.object}).isRequired,
  /**
   * Width of the Dropdown Component, as CSS (e.g. '500px', '50%')
   */
  width: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  /**
   * Max Width of the Dropdown Component, as CSS (e.g. '500px', '50%')
   */
  maxWidth: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  /**
   * On Change function. Function should accept two arguments, dataPoint.name and newValue
   */
  onChange: PropTypes.func,
  /**
   * Whether to show the help icon for the Benchmark disclosure modal
   */
  showDisclosure: PropTypes.bool,
  layout: layoutAttribute.propTypes,
};
